import { HeaderConfigSnapshot } from '../contracts/header';
import { DEFAULT_HEADER_CONFIG } from './default-menu';

const STORAGE_PREFIX = 'vidau_hdr_';
const STORAGE_TTL_MS = 30 * 60 * 1000; // sessionStorage 缓存 30 分钟

export class ConfigClient {
  private static cache: Map<string, { config: HeaderConfigSnapshot; timestamp: number }> = new Map();
  private static CACHE_TTL_MS = 5 * 60 * 1000; // 内存缓存 5 分钟

  private static resolveTargetUrl(
    configUrl?: string,
    locale: string = 'en-US',
    env?: 'production' | 'development'
  ): string {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    const isTestDeployment = hostname.includes('vidau.info');
    const ssoDomain = env === 'development'
      ? 'https://sso.vidau.info'
      : env === 'production'
        ? 'https://sso.vidau.ai'
        : isLocal
          ? window.location.origin
          : isTestDeployment
            ? 'https://sso.vidau.info'
            : 'https://sso.vidau.ai';
    return configUrl || `${ssoDomain}/api/header-config/v1/production/${locale}/current.json`;
  }

  /** 从 sessionStorage 读取缓存（跨页面跳转保留） */
  private static readStorage(key: string): HeaderConfigSnapshot | null {
    try {
      const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
      if (!raw) return null;
      const { config, ts } = JSON.parse(raw);
      if (Date.now() - ts > STORAGE_TTL_MS) {
        sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
        return null;
      }
      return config;
    } catch {
      return null;
    }
  }

  /** 写入 sessionStorage */
  private static writeStorage(key: string, config: HeaderConfigSnapshot) {
    try {
      sessionStorage.setItem(
        `${STORAGE_PREFIX}${key}`,
        JSON.stringify({ config, ts: Date.now() })
      );
    } catch {
      // quota exceeded, silently ignore
    }
  }

  /** 同步检查是否有可用的缓存（内存或 sessionStorage） */
  public static hasCachedConfig(
    configUrl?: string,
    locale: string = 'en-US',
    env?: 'production' | 'development'
  ): boolean {
    const targetUrl = this.resolveTargetUrl(configUrl, locale, env);

    const memCached = this.cache.get(targetUrl);
    if (memCached && Date.now() - memCached.timestamp < this.CACHE_TTL_MS) return true;

    return this.readStorage(targetUrl) !== null;
  }

  /**
   * 获取菜单配置（带两级缓存）
   * @returns 立即返回缓存数据或远程数据
   */
  public static async fetchConfig(
    configUrl?: string,
    locale: string = 'en-US',
    env?: 'production' | 'development'
  ): Promise<HeaderConfigSnapshot> {
    const targetUrl = this.resolveTargetUrl(configUrl, locale, env);

    // L1: 内存缓存（同页面内极速命中）
    const memCached = this.cache.get(targetUrl);
    if (memCached && Date.now() - memCached.timestamp < this.CACHE_TTL_MS) {
      return memCached.config;
    }

    // L2: sessionStorage 缓存（跨页面跳转命中）
    const storageCached = this.readStorage(targetUrl);
    if (storageCached) {
      // 写回内存缓存
      this.cache.set(targetUrl, { config: storageCached, timestamp: Date.now() });
      return storageCached;
    }

    // L3: 远程获取
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);

      const response = await fetch(targetUrl, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HeaderConfigSnapshot = await response.json();
      if (!data || !data.variants) {
        throw new Error('Invalid config format received');
      }

      this.cache.set(targetUrl, { config: data, timestamp: Date.now() });
      this.writeStorage(targetUrl, data);
      return data;
    } catch (error) {
      console.warn('[VidAU Header SDK] Failed to load remote config, using fallback:', error);
      return DEFAULT_HEADER_CONFIG;
    }
  }
}
